/** @jsxRuntime classic */
/** @jsx jsx */
import { useRef, useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';

const FILL_COLOR = '#2028FA';
const GRAY_COLOR = '#D3D3D3';

function startDrawing(ctx, width, height, analyser) {
  let stop = false;
  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);
  const draw = () => {
    analyser.getByteFrequencyData(dataArray);
    ctx.clearRect(0, 0, width, height);
    let barHeight = 0;
    let x = 0;
    const sampleRate = 44100;
    const minHz = 1000;
    const maxHz = 7000;
    const maxI = Math.floor((bufferLength * maxHz) / sampleRate / 2);
    const minI = Math.floor((bufferLength * minHz) / sampleRate / 2);
    const barWidth = width / (maxI - minI);

    for (let i = minI; i < maxI; i++) {
      ctx.fillStyle = FILL_COLOR;
      x += barWidth + 1;
      const value = dataArray[i] / 255;
      barHeight = Math.max(1, Math.abs((value * height) / 2));

      const radius = Math.min(barWidth / 2, barHeight / 2, 4);

      // Draw rounded rectangle
      ctx.beginPath();
      ctx.roundRect(x, height / 2 - barHeight / 2, barWidth, barHeight, radius);
      ctx.fill();
    }

    if (!stop) {
      window.requestAnimationFrame(draw);
    }
  };

  window.requestAnimationFrame(draw);
  return () => {
    stop = true;
  };
}

async function drawDashedLine(ctx, width, height, color = GRAY_COLOR) {
  const dashLength = 2;
  const dashGap = 2;
  let x = 0;
  let y = height / 2;
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  while (x < width) {
    ctx.moveTo(x, y);
    x += dashLength;
    ctx.lineTo(x, y);
    x += dashGap;
  }
  ctx.stroke();
}

// Render a waveform from a MediaRecorder instance
export default function AudioWave({ recorder }) {
  const canvasRef = useRef(null);

  const [audioContext, setAudioContext] = useState(null);

  useEffect(() => {
    let stop;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.clientWidth;
    const height = 88;

    canvas.width = width;
    canvas.height = height;

    if (recorder) {
      const stream = recorder.stream;
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0.85;
      const source = audioContext.createMediaStreamSource(stream);

      source.connect(analyser);

      if (stop) {
        stop();
      }

      if (recorder.state === 'recording') {
        stop = startDrawing(ctx, width, height, analyser);
      } else {
        setTimeout(() => {
          //clear the canvas
          ctx.clearRect(0, 0, width, height);
          drawDashedLine(ctx, width, height);
        }, 50);
      }
      setAudioContext(audioContext);
    } else {
      // draw a dashed line
      ctx.clearRect(0, 0, width, height);
      drawDashedLine(ctx, width, height);
    }

    return () => {
      audioContext?.close();
      setAudioContext(null);
      if (stop) stop();
    };
  }, [recorder, recorder?.state]);

  return (
    <canvas
      ref={canvasRef}
      css={css`
        width: 100%;
        height: 100%;
      `}
    />
  );
}
